import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Chip, CircularProgress, TextField, MenuItem,
  Card, CardContent, IconButton, Collapse, Alert, Badge, Dialog, Button,
  Tabs, Tab, Tooltip, Stepper, Step, StepLabel,
} from '@mui/material';
import {
  BugReport as BugIcon,
  Lightbulb as FeatureIcon,
  Tune as ImprovementIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Image as ImageIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  SmartToy as AiIcon,
  Person as HumanIcon,
  Code as CodeIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  TaskAlt as DoneIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import Markdown from 'react-markdown';
import { AI_ENGINE_URL, BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { BacklogBoard } from '../Backlog/Backlog';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';

const TYPE_CONFIG = {
  bug: { label: 'Bug', icon: <BugIcon />, color: '#ef4444', bg: '#fef2f2' },
  feature: { label: 'Nieuw idee', icon: <FeatureIcon />, color: '#f59e0b', bg: '#fffbeb' },
  improvement: { label: 'Verbetering', icon: <ImprovementIcon />, color: '#6366f1', bg: '#eef2ff' },
};

const PRIORITY_COLORS = {
  low: { label: 'Laag', color: '#64748b' },
  medium: { label: 'Normaal', color: '#3b82f6' },
  high: { label: 'Hoog', color: '#ef4444' },
};

const STATUS_OPTIONS = [
  { value: '', label: 'Alle' },
  { value: 'new', label: 'Nieuw' },
  { value: 'in_progress', label: 'In behandeling' },
  { value: 'done', label: 'Afgerond' },
  { value: 'dismissed', label: 'Afgewezen' },
];

// Status-chip in de kop zodra beheer de melding heeft afgehandeld.
const STATUS_CHIP = {
  done: { label: 'Afgerond', color: '#059669' },
  dismissed: { label: 'Niet opgepakt', color: '#64748b' },
};
const statusLabel = (st) => STATUS_OPTIONS.find(o => o.value === st)?.label || st;

const PIPELINE_STEPS = [
  { key: 'new', label: 'Nieuw' },
  { key: 'plan', label: 'Plan' },
  { key: 'validatie', label: 'Check' },
  { key: 'review', label: 'Review' },
  { key: 'implementatie', label: 'Bouwen' },
  { key: 'pr_open', label: 'Op test' },
  { key: 'merged_to_develop', label: 'Testen' },
  { key: 'approved_for_prod', label: 'Goedgekeurd' },
  { key: 'done', label: 'Live' },
];

// Website pipeline has fewer steps (no develop/test, PR merges directly to master)
const PIPELINE_STEPS_WEBSITE = [
  { key: 'new', label: 'Nieuw' },
  { key: 'plan', label: 'Plan' },
  { key: 'validatie', label: 'Check' },
  { key: 'review', label: 'Review' },
  { key: 'implementatie', label: 'Bouwen' },
  { key: 'pr_open', label: 'Op test' },
  { key: 'merged_to_main', label: 'Live' },
  { key: 'done', label: 'Live' },
];

// Map each pipeline_status to a stepper index
const STATUS_TO_STEP = {
  new: 0,
  plan_started: 1, plan_created: 1, plan_failed: 1,
  validation_started: 2, validation_approved: 2, validation_rejected: 2,
  awaiting_review: 3,
  implementation_started: 4,
  pr_open: 5, pr_failed: 5,
  merged_to_develop: 6, deployed_to_test: 6,
  approved_for_prod: 7, merged_to_main: 7,
  done: 8,
};

const STATUS_TO_STEP_WEBSITE = {
  new: 0,
  plan_started: 1, plan_created: 1, plan_failed: 1,
  validation_started: 2, validation_approved: 2, validation_rejected: 2,
  awaiting_review: 3,
  implementation_started: 4,
  pr_open: 5, pr_failed: 5,
  merged_to_main: 6,
  done: 7,
};

const REPO_LABELS = {
  'ff-material-app': { label: 'Intern', color: '#6366f1', icon: 'wrench' },
  'websiteff': { label: 'Website', color: '#06b6d4', icon: 'globe' },
};

const PIPELINE_STATUS_LABELS = {
  new: { label: 'Nieuw', color: '#94a3b8' },
  plan_started: { label: 'Plan bezig...', color: '#a78bfa' },
  plan_created: { label: 'Plan klaar', color: '#8b5cf6' },
  plan_failed: { label: 'Plan mislukt', color: '#ef4444' },
  validation_started: { label: 'Validatie bezig...', color: '#60a5fa' },
  validation_approved: { label: 'Validatie OK', color: '#3b82f6' },
  validation_rejected: { label: 'Validatie afgewezen', color: '#ef4444' },
  awaiting_review: { label: 'Wacht op review', color: '#f59e0b' },
  implementation_started: { label: 'Implementatie bezig...', color: '#f59e0b' },
  pr_open: { label: 'Op test', color: '#f59e0b' },
  pr_failed: { label: 'Bouwen mislukt', color: '#ef4444' },
  merged_to_develop: { label: 'Wordt getest', color: '#3b82f6' },
  deployed_to_test: { label: 'Op test-omgeving', color: '#06b6d4' },
  approved_for_prod: { label: 'Goedgekeurd', color: '#10b981' },
  merged_to_main: { label: 'Live', color: '#059669' },
  done: { label: 'Live', color: '#22c55e' },
  rejected: { label: 'Afgewezen', color: '#ef4444' },
};

const FAILED_STATUSES = ['plan_failed', 'pr_failed', 'validation_rejected', 'rejected'];
const IN_PROGRESS_STATUSES = ['plan_started', 'plan_created', 'validation_started', 'validation_approved', 'awaiting_review', 'implementation_started'];

function getPipelineStep(status, targetRepo) {
  if (targetRepo === 'websiteff') return STATUS_TO_STEP_WEBSITE[status] ?? 0;
  return STATUS_TO_STEP[status] ?? 0;
}

function getPipelineSteps(targetRepo) {
  return targetRepo === 'websiteff' ? PIPELINE_STEPS_WEBSITE : PIPELINE_STEPS;
}

export default function FeedbackOverview() {
  // Read URL params for deep linking (e.g. ?tab=feedback&view=pipeline&id=5)
  const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const deepLinkView = urlParams.get('view');
  const deepLinkId = urlParams.get('id');

  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState({});
  const [pipelineItems, setPipelineItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState(deepLinkId ? '' : 'new'); // show all when deep linking
  const [filterTool, setFilterTool] = useState('');
  const [expandedId, setExpandedId] = useState(deepLinkId ? parseInt(deepLinkId, 10) : null);
  const [previewImage, setPreviewImage] = useState(null);
  const [viewTab, setViewTab] = useState(deepLinkView === 'pipeline' ? 1 : deepLinkView === 'backlog' ? 2 : 0);
  const [actionLoading, setActionLoading] = useState({});
  const [pipelineFilter, setPipelineFilter] = useState(''); // filter pipeline by status
  const [filterRepo, setFilterRepo] = useState(''); // filter by target_repo

  // Persist subtab in URL without page reload
  const setViewTabAndUrl = useCallback((newTab) => {
    setViewTab(newTab);
    const params = new URLSearchParams(window.location.search);
    if (newTab === 1) {
      params.set('view', 'pipeline');
    } else if (newTab === 2) {
      params.set('view', 'backlog');
    } else {
      params.delete('view');
    }
    const newUrl = `${window.location.pathname}?${params}`;
    window.history.replaceState(null, '', newUrl);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType) params.set('feedback_type', filterType);
      if (filterStatus) params.set('status', filterStatus);
      if (filterTool) params.set('tool_name', filterTool);
      if (filterRepo) params.set('target_repo', filterRepo);
      params.set('limit', '100');

      const pipelineParams = new URLSearchParams({ limit: '100' });
      if (filterRepo) pipelineParams.set('target_repo', filterRepo);

      const fetches = [
        // Admin list: all feedback via Node.js backend (JWT + admin role check)
        authFetch(`${BACKEND_URL}/Api/feedback/all?${params}`),
        // Stats: aggregate only, no PII
        authFetch(`${AI_ENGINE_URL}/api/v1/tool-feedback/stats`),
      ];

      // Pipeline data — always fetch for status display in both tabs
      fetches.push(authFetch(`${BACKEND_URL}/Api/feedback/pipeline?${pipelineParams}`));

      const results = await Promise.allSettled(fetches);

      // Process each result independently so one failure doesn't block others
      if (results[0]?.status === 'fulfilled' && results[0].value.ok) {
        setFeedback(await results[0].value.json());
      } else {
        console.warn('[Feedback] Feedback list failed:', results[0]?.reason || results[0]?.value?.status);
      }
      if (results[1]?.status === 'fulfilled' && results[1].value.ok) {
        setStats(await results[1].value.json());
      } else {
        console.warn('[Feedback] Stats failed:', results[1]?.reason || results[1]?.value?.status);
      }
      if (results[2]?.status === 'fulfilled' && results[2].value.ok) {
        setPipelineItems(await results[2].value.json());
      } else {
        console.warn('[Feedback] Pipeline failed:', results[2]?.reason || results[2]?.value?.status);
      }
    } catch (err) {
      console.error('[Feedback] Error loading:', err);
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStatus, filterTool, filterRepo]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => registreerOngedaanSneltoets(), []);

  // Auto-refresh pipeline data silently (no loading flash, preserves expanded state)
  const hasRunning = pipelineItems.some(i => IN_PROGRESS_STATUSES.includes(i.pipeline_status));
  useEffect(() => {
    if (!hasRunning) return;
    const interval = setInterval(async () => {
      try {
        const pipelineParams = new URLSearchParams({ limit: '100' });
        if (filterRepo) pipelineParams.set('target_repo', filterRepo);
        const res = await authFetch(`${BACKEND_URL}/Api/feedback/pipeline?${pipelineParams}`);
        if (res.ok) setPipelineItems(await res.json());
      } catch (err) { /* silent */ }
    }, 15000);
    return () => clearInterval(interval);
  }, [hasRunning, filterRepo]); // eslint-disable-line

  // Auto-expand: only on FIRST load when user hasn't manually expanded anything
  const [hasAutoExpanded, setHasAutoExpanded] = useState(false);
  useEffect(() => {
    if (hasAutoExpanded || expandedId) return; // user already picked something
    const running = pipelineItems.find(i => IN_PROGRESS_STATUSES.includes(i.pipeline_status));
    if (running) {
      setExpandedId(running.id);
      setHasAutoExpanded(true);
    }
  }, [pipelineItems, expandedId, hasAutoExpanded]);

  // Build pipeline lookup map for Feedback tab
  const pipelineMap = {};
  pipelineItems.forEach(item => { pipelineMap[item.id] = item; });

  // Optimistic update helper — immediately update pipeline status in UI
  const optimisticUpdate = (id, newStatus) => {
    const updater = (items) => items.map(i => i.id === id ? { ...i, pipeline_status: newStatus } : i);
    setPipelineItems(updater);
    setFeedback(updater);
  };

  const handleApprove = async (id) => {
    setActionLoading(prev => ({ ...prev, [id]: 'approve' }));
    optimisticUpdate(id, 'approved_for_prod');
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/feedback/${id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || err.detail || `HTTP ${res.status}`);
      }
      toast.success('Goedgekeurd voor productie');
      fetchData();
    } catch (err) {
      toast.error(`Goedkeuring mislukt: ${err.message}`);
      fetchData(); // revert optimistic update
    } finally {
      setActionLoading(prev => ({ ...prev, [id]: null }));
    }
  };

  const handleApproveReview = async (id) => {
    setActionLoading(prev => ({ ...prev, [id]: 'approve_review' }));
    optimisticUpdate(id, 'implementation_started');
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/feedback/${id}/user-approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || err.detail || `HTTP ${res.status}`);
      }
      toast.success('Plan goedgekeurd — implementatie gestart');
      fetchData();
    } catch (err) {
      toast.error(`Goedkeuring mislukt: ${err.message}`);
      fetchData();
    } finally {
      setActionLoading(prev => ({ ...prev, [id]: null }));
    }
  };

  const handleStartPipeline = async (id) => {
    setActionLoading(prev => ({ ...prev, [id]: 'start' }));
    optimisticUpdate(id, 'plan_started');
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/feedback/${id}/start-pipeline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || err.detail || `HTTP ${res.status}`);
      }
      toast.success('Pipeline gestart — plan wordt aangemaakt');
    } catch (err) {
      toast.error(`Pipeline starten mislukt: ${err.message}`);
      fetchData(); // revert optimistic update
    } finally {
      setActionLoading(prev => ({ ...prev, [id]: null }));
    }
  };

  const handleReject = async (id) => {
    setActionLoading(prev => ({ ...prev, [id]: 'reject' }));
    optimisticUpdate(id, 'rejected');
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/feedback/${id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: 'Afgewezen door admin' }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || err.detail || `HTTP ${res.status}`);
      }
      toast.success('Feedback afgewezen');
    } catch (err) {
      toast.error(`Afwijzing mislukt: ${err.message}`);
      fetchData(); // revert optimistic update
    } finally {
      setActionLoading(prev => ({ ...prev, [id]: null }));
    }
  };

  // Status zelf zetten (los van de pipeline), bv. na een handmatige fix.
  // De melding blijft in de lijst staan tot verversen, zodat je ziet wat er gebeurde.
  const handleStatus = async (item, status, notes) => {
    const vorige = item.status || 'new';
    const zet = async (nieuw, toelichting) => {
      const res = await authFetch(`${BACKEND_URL}/Api/feedback/${item.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: nieuw, notes: toelichting || null }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || data.detail || `HTTP ${res.status}`);
      const bij = (i) => (i.id === item.id ? { ...i, status: nieuw, admin_notes: data.admin_notes ?? i.admin_notes } : i);
      setFeedback(lijst => lijst.map(bij));
      setPipelineItems(lijst => lijst.map(bij));
    };
    setActionLoading(prev => ({ ...prev, [item.id]: 'status' }));
    try {
      await zet(status, notes);
      biedOngedaanAan(`#${item.id} → ${statusLabel(status)}`, () => zet(vorige, 'ongedaan gemaakt'));
    } catch (err) {
      toast.error(`Status wijzigen mislukt: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [item.id]: null }));
    }
  };

  // Unique tool names from stats
  const toolNames = Object.keys(stats).sort();
  const totalCount = Object.values(stats).reduce((sum, s) => sum + (s.total || 0), 0);

  // Pipeline stats
  const pipelineCounts = {};
  pipelineItems.forEach(item => {
    const st = item.pipeline_status || 'new';
    pipelineCounts[st] = (pipelineCounts[st] || 0) + 1;
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* View toggle */}
      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Tabs value={viewTab} onChange={(_, v) => setViewTabAndUrl(v)}>
          <Tab label="Feedback" />
          <Tab label={`Pipeline${pipelineItems.length ? ` (${pipelineItems.length})` : ''}`} />
          <Tab label="Backlog" />
        </Tabs>
      </Paper>

      {/* Pipeline status dashboard — visible on Feedback + Pipeline, not Backlog */}
      {viewTab !== 2 && pipelineItems.length > 0 && (() => {
        const failed = pipelineItems.filter(i => FAILED_STATUSES.includes(i.pipeline_status));
        const running = pipelineItems.filter(i => IN_PROGRESS_STATUSES.includes(i.pipeline_status));
        const review = pipelineItems.filter(i => i.pipeline_status === 'awaiting_review');
        const onTest = pipelineItems.filter(i => ['pr_open', 'merged_to_develop', 'deployed_to_test'].includes(i.pipeline_status));
        if (failed.length === 0 && running.length === 0 && review.length === 0 && onTest.length === 0) return null;
        return (
          <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#f8fafc' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Pipeline Status</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {failed.length > 0 && (
                <Chip label={`${failed.length} mislukt`} size="small"
                  sx={{ bgcolor: '#fef2f2', color: '#ef4444', fontWeight: 600, cursor: 'pointer' }}
                  onClick={() => { setViewTabAndUrl(1); setPipelineFilter('failed'); }} />
              )}
              {running.length > 0 && (
                <Chip label={`${running.length} bezig`} size="small"
                  sx={{ bgcolor: '#eef2ff', color: '#6366f1', fontWeight: 600, cursor: 'pointer' }}
                  onClick={() => { setViewTabAndUrl(1); setPipelineFilter('in_progress'); }} />
              )}
              {review.length > 0 && (
                <Chip label={`${review.length} wacht op review`} size="small"
                  sx={{ bgcolor: '#fffbeb', color: '#d97706', fontWeight: 600, cursor: 'pointer' }}
                  onClick={() => { setViewTabAndUrl(1); setPipelineFilter('awaiting_review'); }} />
              )}
              {onTest.length > 0 && (
                <Chip label={`${onTest.length} op test`} size="small"
                  sx={{ bgcolor: '#ecfdf5', color: '#059669', fontWeight: 600, cursor: 'pointer' }}
                  onClick={() => { setViewTabAndUrl(1); setPipelineFilter('on_test'); }} />
              )}
            </Box>
          </Paper>
        );
      })()}

      {viewTab === 0 && (
        <>
          {/* Stats summary */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, flex: 1, minWidth: 140 }}>
              <Typography variant="caption" color="text.secondary">Totaal</Typography>
              <Typography variant="h5" fontWeight={700}>{totalCount}</Typography>
            </Paper>
            {Object.entries(TYPE_CONFIG).map(([type, cfg]) => {
              const count = Object.values(stats).reduce((sum, s) => sum + (s[type] || 0), 0);
              return (
                <Paper key={type} variant="outlined" sx={{ px: 2.5, py: 1.5, flex: 1, minWidth: 140, borderLeft: `3px solid ${cfg.color}` }}>
                  <Typography variant="caption" color="text.secondary">{cfg.label}</Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ color: cfg.color }}>{count}</Typography>
                </Paper>
              );
            })}
          </Box>

          {/* Filters */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField select size="small" label="Project" value={filterRepo}
              onChange={(e) => setFilterRepo(e.target.value)} sx={{ minWidth: 140 }}>
              <MenuItem value="">Alle projecten</MenuItem>
              <MenuItem value="ff-material-app">Intern App</MenuItem>
              <MenuItem value="websiteff">Website</MenuItem>
            </TextField>
            <TextField select size="small" label="Status" value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)} sx={{ minWidth: 140 }}>
              {STATUS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Type" value={filterType}
              onChange={(e) => setFilterType(e.target.value)} sx={{ minWidth: 140 }}>
              <MenuItem value="">Alle</MenuItem>
              <MenuItem value="bug">Bug</MenuItem>
              <MenuItem value="feature">Nieuw idee</MenuItem>
              <MenuItem value="improvement">Verbetering</MenuItem>
            </TextField>
            <TextField select size="small" label="Tool" value={filterTool}
              onChange={(e) => setFilterTool(e.target.value)} sx={{ minWidth: 180 }}>
              <MenuItem value="">Alle tools</MenuItem>
              {toolNames.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center', ml: 'auto' }}>
              {feedback.length} resultaten
            </Typography>
          </Paper>

          {/* Feedback list */}
          {feedback.length === 0 ? (
            <Alert severity="info">Geen feedback gevonden met deze filters.</Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {feedback.map((item) => (
                <FeedbackCard
                  key={item.id}
                  item={item}
                  pipelineData={pipelineMap[item.id]}
                  isExpanded={expandedId === item.id}
                  onToggle={() => setExpandedId(expandedId === item.id ? null : item.id)}
                  onPreview={setPreviewImage}
                  onApprove={() => handleApprove(item.id)}
                  onApproveReview={() => handleApproveReview(item.id)}
                  onReject={() => handleReject(item.id)}
                  onStartPipeline={() => handleStartPipeline(item.id)}
                  onStatus={(status, notes) => handleStatus(item, status, notes)}
                  actionLoading={actionLoading[item.id]}
                />
              ))}
            </Box>
          )}
        </>
      )}

      {viewTab === 1 && (
        <>
          {/* Pipeline header with refresh */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1 }}>
              {pipelineItems.length} items in pipeline
            </Typography>
            <Tooltip title="Vernieuwen">
              <IconButton size="small" onClick={fetchData} disabled={loading}>
                <RefreshIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Pipeline status counts — clickable to filter */}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
            {Object.entries(PIPELINE_STATUS_LABELS).map(([key, cfg]) => {
              const count = pipelineCounts[key] || 0;
              if (count === 0 && !['new', 'deployed_to_test', 'pr_open', 'pr_failed'].includes(key)) return null;
              const isActive = pipelineFilter === key;
              return (
                <Paper
                  key={key}
                  variant="outlined"
                  onClick={() => setPipelineFilter(isActive ? '' : key)}
                  sx={{
                    px: 2, py: 1, minWidth: 100, cursor: 'pointer',
                    borderLeft: `3px solid ${cfg.color}`,
                    bgcolor: isActive ? `${cfg.color}10` : 'transparent',
                    outline: isActive ? `2px solid ${cfg.color}` : 'none',
                    transition: 'all 0.15s',
                    '&:hover': { bgcolor: `${cfg.color}08` },
                  }}
                >
                  <Typography variant="caption" color="text.secondary">{cfg.label}</Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: cfg.color }}>{count}</Typography>
                </Paper>
              );
            })}
          </Box>

          {/* Pipeline items */}
          {pipelineItems.length === 0 ? (
            <Alert severity="info">Geen items in de pipeline.</Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {pipelineItems.filter(item => !pipelineFilter || item.pipeline_status === pipelineFilter).map((item) => (
                <PipelineCard
                  key={item.id}
                  item={item}
                  isExpanded={expandedId === item.id}
                  onToggle={() => setExpandedId(expandedId === item.id ? null : item.id)}
                  onPreview={setPreviewImage}
                  onApprove={() => handleApprove(item.id)}
                  onApproveReview={() => handleApproveReview(item.id)}
                  onReject={() => handleReject(item.id)}
                  onStartPipeline={() => handleStartPipeline(item.id)}
                  actionLoading={actionLoading[item.id]}
                />
              ))}
            </Box>
          )}
        </>
      )}

      {viewTab === 2 && (
        <BacklogBoard embedded />
      )}

      {/* Screenshot preview dialog */}
      {/* Geen maxWidth="lg": die kapte brede screenshots aan de rechterkant af (Joris, 15 sep). */}
      <Dialog open={!!previewImage} onClose={() => setPreviewImage(null)} maxWidth={false}
        PaperProps={{ sx: { maxWidth: '96vw', maxHeight: '96vh', m: 1, bgcolor: '#0f172a' } }}>
        <Box sx={{ position: 'relative' }}>
          <IconButton
            onClick={() => setPreviewImage(null)}
            sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
          >
            <CloseIcon />
          </IconButton>
          {previewImage && (
            <img src={previewImage} alt="Screenshot preview" style={{ maxWidth: '96vw', maxHeight: '92vh', width: 'auto', height: 'auto', display: 'block', objectFit: 'contain' }} />
          )}
        </Box>
      </Dialog>
    </Box>
  );
}


// ── Pipeline detail sections (plan, validation, error) ──────────────────────

const mdSx = {
  '& h1': { fontSize: '1rem', fontWeight: 700, mt: 1, mb: 0.5 },
  '& h2': { fontSize: '0.85rem', fontWeight: 600, mt: 1.5, mb: 0.5, color: '#475569' },
  '& p': { fontSize: '0.8rem', my: 0.5, lineHeight: 1.5 },
  '& ul': { pl: 2, my: 0.5 },
  '& li': { fontSize: '0.8rem', mb: 0.25 },
  '& code': { bgcolor: '#f1f5f9', px: 0.5, py: 0.25, borderRadius: 0.5, fontSize: '0.75rem' },
};

function PipelineDetails({ item }) {
  const isRunning = IN_PROGRESS_STATUSES.includes(item?.pipeline_status);
  const [showPlan, setShowPlan] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  const hasPlan = !!item?.plan_summary;
  const hasValidation = !!item?.validation_result;
  const hasError = !!item?.error_detail;

  // Auto-open latest available section when data arrives
  React.useEffect(() => {
    if (hasValidation) setShowValidation(true);
    else if (hasPlan) setShowPlan(true);
  }, [hasPlan, hasValidation]);

  if (!hasPlan && !hasValidation && !hasError) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
      {/* Error detail */}
      {hasError && (
        <Alert severity="error" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>Foutmelding</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{item.error_detail}</Typography>
        </Alert>
      )}

      {/* Plan summary */}
      {hasPlan && (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <Box
            onClick={() => setShowPlan(!showPlan)}
            sx={{ px: 2, py: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', bgcolor: '#f8fafc', '&:hover': { bgcolor: '#f1f5f9' } }}
          >
            <CodeIcon sx={{ fontSize: 16, mr: 1, color: '#8b5cf6' }} />
            <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>Implementatieplan</Typography>
            <IconButton size="small">{showPlan ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
          </Box>
          <Collapse in={showPlan}>
            <Box sx={{ px: 2, py: 1, ...mdSx }}>
              <Markdown>{item.plan_summary}</Markdown>
            </Box>
          </Collapse>
        </Paper>
      )}

      {/* Validation result */}
      {hasValidation && (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <Box
            onClick={() => setShowValidation(!showValidation)}
            sx={{ px: 2, py: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', bgcolor: '#f8fafc', '&:hover': { bgcolor: '#f1f5f9' } }}
          >
            <ApproveIcon sx={{ fontSize: 16, mr: 1, color: '#3b82f6' }} />
            <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>Validatie resultaat</Typography>
            <IconButton size="small">{showValidation ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
          </Box>
          <Collapse in={showValidation}>
            <Box sx={{ px: 2, py: 1, ...mdSx }}>
              <Markdown>{item.validation_result}</Markdown>
            </Box>
          </Collapse>
        </Paper>
      )}
    </Box>
  );
}

// ── Feedback Card (original view) ───────────────────────────────────────────

/** Filter out meaningless follow-up values (empty, dot, single char) */
function filterFollowUps(followUps) {
  if (!followUps || typeof followUps !== 'object') return {};
  const filtered = {};
  Object.entries(followUps).forEach(([key, value]) => {
    if (value && typeof value === 'string' && value.trim().length > 2) {
      filtered[key] = value;
    }
  });
  return filtered;
}

function FeedbackCard({ item, pipelineData, isExpanded, onToggle, onPreview, onApprove, onApproveReview, onReject, onStartPipeline, onStatus, actionLoading }) {
  const [toelichting, setToelichting] = useState('');
  const type = TYPE_CONFIG[item.feedback_type] || TYPE_CONFIG.improvement;
  const prio = PRIORITY_COLORS[item.priority] || PRIORITY_COLORS.medium;
  const hasScreenshots = item.screenshot_urls?.length > 0;
  const followUps = filterFollowUps(item.follow_up_answers);
  const date = new Date(item.created_at);
  const repo = REPO_LABELS[item.target_repo] || REPO_LABELS['ff-material-app'];

  // Pipeline status from merged pipeline data
  const ps = pipelineData?.pipeline_status || 'new';
  const pipelineStatus = PIPELINE_STATUS_LABELS[ps] || PIPELINE_STATUS_LABELS.new;
  const canStart = ps === 'new';
  const canRetry = FAILED_STATUSES.includes(ps);  // Can retry any failed item
  const canApproveReview = ps === 'awaiting_review';
  const canApprove = ['deployed_to_test', 'pr_open', 'merged_to_develop'].includes(ps);
  const canReject = !['new', 'done', 'rejected', 'merged_to_main'].includes(ps);
  const isFailed = FAILED_STATUSES.includes(ps);
  const isRunning = IN_PROGRESS_STATUSES.includes(ps);
  const hasPipeline = pipelineData && ps !== 'new';
  const targetRepo = item.target_repo || 'ff-material-app';
  const steps = getPipelineSteps(targetRepo);
  const afgehandeld = STATUS_CHIP[item.status];
  const zetStatus = (st) => { onStatus(st, toelichting.trim()); setToelichting(''); };

  return (
    <Card variant="outlined" sx={{ borderLeft: `4px solid ${type.color}`, opacity: afgehandeld ? 0.75 : 1, '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }, transition: 'box-shadow 0.2s' }}>
      <CardContent sx={{ pb: isExpanded ? 2 : '12px !important', pt: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }} onClick={onToggle}>
          {React.cloneElement(type.icon, { sx: { color: type.color, fontSize: 20 } })}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.description}</Typography>
          {hasScreenshots && (
            <Badge badgeContent={item.screenshot_urls.length} color="info">
              <ImageIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
            </Badge>
          )}
          {/* Pipeline status chip (if not new) */}
          {hasPipeline && (
            <Chip
              label={pipelineStatus.label}
              size="small"
              sx={{ bgcolor: `${pipelineStatus.color}15`, color: pipelineStatus.color, fontWeight: 600, fontSize: '0.7rem', borderColor: pipelineStatus.color }}
              variant="outlined"
            />
          )}
          {afgehandeld && (
            <Chip label={afgehandeld.label} size="small"
              sx={{ bgcolor: `${afgehandeld.color}15`, color: afgehandeld.color, fontWeight: 600, fontSize: '0.7rem', borderColor: afgehandeld.color }}
              variant="outlined" />
          )}
          {targetRepo === 'websiteff' && (
            <Chip label={repo.label} size="small" sx={{ bgcolor: `${repo.color}15`, color: repo.color, fontWeight: 500, fontSize: '0.65rem', borderColor: repo.color }} variant="outlined" />
          )}
          <Chip label={item.tool_name} size="small" sx={{ bgcolor: '#e0e7ff', color: '#4338ca', fontWeight: 500, fontSize: '0.7rem' }} />
          <Chip label={prio.label} size="small" sx={{ color: prio.color, borderColor: prio.color, fontSize: '0.7rem' }} variant="outlined" />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            {item.user_email?.split('@')[0]}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60, textAlign: 'right' }}>
            {date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}
          </Typography>
          <IconButton size="small">{isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
        </Box>

        <Collapse in={isExpanded} onClick={(e) => e.stopPropagation()}>
          <Box sx={{ mt: 2, pl: 3.5 }}>
            {/* Pipeline stepper */}
            {hasPipeline && ps !== 'rejected' && (
              <Stepper
                activeStep={getPipelineStep(ps, targetRepo)}
                alternativeLabel
                sx={{
                  mb: 3,
                  '& .MuiStepIcon-root.Mui-active': isRunning ? { color: '#f59e0b', animation: 'pulse 1.5s infinite' } : {},
                  '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } },
                }}
              >
                {steps.map((step, idx) => (
                  <Step key={step.key} completed={idx < getPipelineStep(ps, targetRepo)}>
                    <StepLabel
                      error={isFailed && idx === getPipelineStep(ps, targetRepo)}
                      sx={{ '& .MuiStepLabel-label': { fontSize: '0.7rem' } }}
                      optional={step.model ? <Typography variant="caption" sx={{ fontSize: '0.55rem', color: '#94a3b8' }}>{step.model}</Typography> : undefined}
                    >
                      {step.label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            )}

            {/* Running state */}
            {isRunning && (
              <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 2 }}>
                {ps === 'plan_started' && 'Claude Opus 4.6 genereert een implementatieplan...'}
                {ps === 'validation_started' && 'GPT-5.4 valideert het plan...'}
                {ps === 'implementation_started' && 'Claude Opus 4.6 implementeert de wijzigingen...'}
              </Alert>
            )}

            {/* Pipeline details (plan, validation, errors) */}
            {pipelineData && <PipelineDetails item={pipelineData} />}

            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary">
                Door: <strong>{item.user_email}</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Route: <code style={{ background: '#f1f5f9', padding: '1px 4px', borderRadius: 3 }}>{item.tool_route}</code>
              </Typography>
              <Typography variant="caption" color="text.secondary">{date.toLocaleString('nl-NL')}</Typography>
            </Box>

            {Object.keys(followUps).length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                {Object.entries(followUps).map(([key, value]) => {
                  const label = key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
                  return (
                    <Box key={key}>
                      <Typography variant="caption" sx={{ color: '#6366f1', fontWeight: 600, display: 'block' }}>{label}</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{value}</Typography>
                    </Box>
                  );
                })}
              </Box>
            )}

            {hasScreenshots && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" sx={{ color: '#6366f1', fontWeight: 600, display: 'block', mb: 0.5 }}>Screenshots</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {item.screenshot_urls.map((url, i) => (
                    <Box key={i} sx={{ width: 120, height: 90, borderRadius: 1.5, overflow: 'hidden', border: '1px solid #e2e8f0', cursor: 'pointer', position: 'relative', '&:hover .overlay': { opacity: 1 } }} onClick={() => onPreview(url)}>
                      <img src={url} alt={`Screenshot ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#f1f5f9' }} />
                      <Box className="overlay" sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.2s' }}>
                        <OpenInNewIcon sx={{ color: '#fff' }} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {item.admin_notes && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: '#6366f1', fontWeight: 600, display: 'block' }}>Beheernotities</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>{item.admin_notes}</Typography>
              </Box>
            )}

            {/* Status zelf zetten — los van de pipeline */}
            <Box sx={{ display: 'flex', gap: 1, mt: 1, pt: 1.5, borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField size="small" value={toelichting} onChange={(e) => setToelichting(e.target.value)}
                placeholder="Toelichting (optioneel), bv. opgelost in 2.25.393"
                sx={{ flex: '1 1 260px', '& input': { fontSize: '0.8rem' } }} />
              {item.status !== 'done' && (
                <Button size="small" variant="outlined" color="success" disabled={!!actionLoading}
                  startIcon={actionLoading === 'status' ? <CircularProgress size={16} /> : <DoneIcon />}
                  onClick={() => zetStatus('done')}>
                  Afgerond
                </Button>
              )}
              {!['done', 'dismissed'].includes(item.status) && (
                <Button size="small" variant="text" color="inherit" disabled={!!actionLoading}
                  onClick={() => zetStatus('dismissed')}>
                  Niet oppakken
                </Button>
              )}
              {['done', 'dismissed'].includes(item.status) && (
                <Button size="small" variant="text" disabled={!!actionLoading} startIcon={<UndoIcon />}
                  onClick={() => zetStatus('new')}>
                  Terug naar nieuw
                </Button>
              )}
            </Box>

            {/* Actions */}
            {(canStart || canRetry || canApproveReview || canApprove || canReject) && (
              <Box sx={{ display: 'flex', gap: 1.5, mt: 1, pt: 1.5, borderTop: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
                {canStart && (
                  <Button size="small" variant="contained" startIcon={actionLoading === 'start' ? <CircularProgress size={16} /> : <PlayIcon />}
                    onClick={onStartPipeline} disabled={!!actionLoading}
                    sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' } }}>
                    Start verwerking
                  </Button>
                )}
                {canRetry && (
                  <Button size="small" variant="contained" startIcon={actionLoading === 'start' ? <CircularProgress size={16} /> : <RefreshIcon />}
                    onClick={onStartPipeline} disabled={!!actionLoading}
                    sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}>
                    Opnieuw proberen
                  </Button>
                )}
                {canApproveReview && (
                  <Button size="small" variant="contained"
                    startIcon={actionLoading === 'approve_review' ? <CircularProgress size={16} /> : <ApproveIcon />}
                    onClick={onApproveReview} disabled={!!actionLoading}
                    sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}>
                    Plan goedkeuren → implementeren
                  </Button>
                )}
                {canApprove && (
                  <Button size="small" variant="contained" color="success"
                    startIcon={actionLoading === 'approve' ? <CircularProgress size={16} /> : <ApproveIcon />}
                    onClick={onApprove} disabled={!!actionLoading}>
                    Goedkeuren voor productie
                  </Button>
                )}
                {canReject && (
                  <Button size="small" variant="outlined" color="error"
                    startIcon={actionLoading === 'reject' ? <CircularProgress size={16} /> : <RejectIcon />}
                    onClick={onReject} disabled={!!actionLoading}>
                    Afwijzen
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}


// ── Pipeline Card (automation view) ─────────────────────────────────────────

function PipelineCard({ item, isExpanded, onToggle, onPreview, onApprove, onApproveReview, onReject, onStartPipeline, actionLoading }) {
  const type = TYPE_CONFIG[item.feedback_type] || TYPE_CONFIG.improvement;
  const pipelineStatus = PIPELINE_STATUS_LABELS[item.pipeline_status] || PIPELINE_STATUS_LABELS.new;
  const date = new Date(item.created_at);
  const canApprove = ['deployed_to_test', 'pr_open', 'merged_to_develop'].includes(item.pipeline_status);
  const canApproveReview = item.pipeline_status === 'awaiting_review';
  const canReject = !['done', 'rejected', 'merged_to_main'].includes(item.pipeline_status);
  const canStart = item.pipeline_status === 'new';
  const canRetry = FAILED_STATUSES.includes(item.pipeline_status);
  const isFailed = FAILED_STATUSES.includes(item.pipeline_status);
  const isRunning = IN_PROGRESS_STATUSES.includes(item.pipeline_status);
  const hasScreenshots = item.screenshot_urls?.length > 0;
  const followUps = filterFollowUps(item.follow_up_answers);
  const targetRepo = item.target_repo || 'ff-material-app';
  const repo = REPO_LABELS[targetRepo] || REPO_LABELS['ff-material-app'];
  const steps = getPipelineSteps(targetRepo);

  return (
    <Card variant="outlined" sx={{ borderLeft: `4px solid ${pipelineStatus.color}`, '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }, transition: 'box-shadow 0.2s' }}>
      <CardContent sx={{ pb: isExpanded ? 2 : '12px !important', pt: 1.5 }}>
        {/* Header — klikbaar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }} onClick={onToggle}>
          {React.cloneElement(type.icon, { sx: { color: type.color, fontSize: 20 } })}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.description}
          </Typography>

          {/* AI eligible badge */}
          <Tooltip title={item.ai_eligible ? 'Auto-verwerking' : 'Handmatige review vereist'}>
            {item.ai_eligible
              ? <AiIcon sx={{ fontSize: 18, color: '#8b5cf6' }} />
              : <HumanIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
            }
          </Tooltip>

          {/* Pipeline status chip */}
          <Chip
            label={pipelineStatus.label}
            size="small"
            sx={{ bgcolor: `${pipelineStatus.color}15`, color: pipelineStatus.color, fontWeight: 600, fontSize: '0.7rem', borderColor: pipelineStatus.color }}
            variant="outlined"
          />

          {targetRepo === 'websiteff' && (
            <Chip label={repo.label} size="small" sx={{ bgcolor: `${repo.color}15`, color: repo.color, fontWeight: 500, fontSize: '0.65rem', borderColor: repo.color }} variant="outlined" />
          )}

          <Chip label={item.tool_name} size="small" sx={{ bgcolor: '#e0e7ff', color: '#4338ca', fontWeight: 500, fontSize: '0.7rem' }} />

          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            {item.user_email?.split('@')[0]}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60, textAlign: 'right' }}>
            {date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}
          </Typography>

          <IconButton size="small">{isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
        </Box>

        {/* Expanded details */}
        <Collapse in={isExpanded} onClick={(e) => e.stopPropagation()}>
          <Box sx={{ mt: 2, pl: 3.5 }}>
            {/* Pipeline stepper */}
            {item.pipeline_status !== 'rejected' && (
              <Stepper
                activeStep={getPipelineStep(item.pipeline_status, targetRepo)}
                alternativeLabel
                sx={{
                  mb: 3,
                  '& .MuiStepIcon-root.Mui-active': isRunning ? { color: '#f59e0b', animation: 'pulse 1.5s infinite' } : {},
                  '& .MuiStepIcon-root.Mui-active.Mui-error': { color: '#ef4444' },
                  '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } },
                }}
              >
                {steps.map((step, idx) => (
                  <Step key={step.key} completed={idx < getPipelineStep(item.pipeline_status, targetRepo)}>
                    <StepLabel
                      error={isFailed && idx === getPipelineStep(item.pipeline_status, targetRepo)}
                      sx={{ '& .MuiStepLabel-label': { fontSize: '0.7rem' } }}
                      optional={step.model ? <Typography variant="caption" sx={{ fontSize: '0.55rem', color: '#94a3b8' }}>{step.model}</Typography> : undefined}
                    >
                      {step.label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            )}

            {/* Running state */}
            {isRunning && (
              <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 2 }}>
                {item.pipeline_status === 'plan_started' && 'Claude Opus 4.6 genereert een implementatieplan...'}
                {item.pipeline_status === 'validation_started' && 'GPT-5.4 valideert het plan...'}
                {item.pipeline_status === 'implementation_started' && 'Claude Opus 4.6 implementeert de wijzigingen...'}
              </Alert>
            )}

            {/* Pipeline details (plan, validation, errors) */}
            <PipelineDetails item={item} />

            {/* Meta info */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary">
                Door: <strong>{item.user_email}</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Route: <code style={{ background: '#f1f5f9', padding: '1px 4px', borderRadius: 3 }}>{item.tool_route}</code>
              </Typography>
              <Typography variant="caption" color="text.secondary">{date.toLocaleString('nl-NL')}</Typography>
            </Box>

            {/* GitHub references */}
            {(item.github_issue_url || item.github_pr_url) && (
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                {item.github_issue_url && (
                  <Chip
                    icon={<CodeIcon sx={{ fontSize: 14 }} />}
                    label={`Issue #${item.github_issue_number}`}
                    size="small"
                    component="a"
                    href={item.github_issue_url}
                    target="_blank"
                    clickable
                    sx={{ fontSize: '0.7rem' }}
                  />
                )}
                {item.github_pr_url && (
                  <Chip
                    icon={<CodeIcon sx={{ fontSize: 14 }} />}
                    label={`PR #${item.github_pr_number}`}
                    size="small"
                    component="a"
                    href={item.github_pr_url}
                    target="_blank"
                    clickable
                    sx={{ fontSize: '0.7rem' }}
                  />
                )}
              </Box>
            )}

            {/* Safety flags */}
            {item.safety_flags?.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2, py: 0.5, '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
                Safety flags: {item.safety_flags.join(', ')}
              </Alert>
            )}

            {/* Admin notes */}
            {item.admin_notes && (
              <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: '#f8fafc' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: '#64748b' }}>Admin notities</Typography>
                <Typography variant="body2">{item.admin_notes}</Typography>
              </Paper>
            )}

            {/* Timestamps */}
            <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
              {item.processed_at && (
                <Typography variant="caption" color="text.secondary">Verwerkt: {new Date(item.processed_at).toLocaleString('nl-NL')}</Typography>
              )}
              {item.merged_at && (
                <Typography variant="caption" color="text.secondary">Gemerged: {new Date(item.merged_at).toLocaleString('nl-NL')}</Typography>
              )}
              {item.deployed_at && (
                <Typography variant="caption" color="text.secondary">Deployed: {new Date(item.deployed_at).toLocaleString('nl-NL')}</Typography>
              )}
              {item.approved_at && (
                <Typography variant="caption" color="text.secondary">Goedgekeurd: {new Date(item.approved_at).toLocaleString('nl-NL')} door {item.approved_by}</Typography>
              )}
            </Box>

            {/* Test URL */}
            {item.test_url && (
              <Button
                size="small"
                variant="outlined"
                href={item.test_url}
                target="_blank"
                sx={{ mb: 2 }}
                startIcon={<OpenInNewIcon />}
              >
                Bekijk op test
              </Button>
            )}

            {/* Follow-up answers */}
            {Object.keys(followUps).length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                {Object.entries(followUps).map(([key, value]) => {
                  if (!value) return null;
                  const label = key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
                  return (
                    <Box key={key}>
                      <Typography variant="caption" sx={{ color: '#6366f1', fontWeight: 600, display: 'block' }}>{label}</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{value}</Typography>
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* Screenshots */}
            {hasScreenshots && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: '#6366f1', fontWeight: 600, display: 'block', mb: 0.5 }}>Screenshots</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {item.screenshot_urls.map((url, i) => (
                    <Box key={i} sx={{ width: 120, height: 90, borderRadius: 1.5, overflow: 'hidden', border: '1px solid #e2e8f0', cursor: 'pointer', position: 'relative', '&:hover .overlay': { opacity: 1 } }} onClick={() => onPreview(url)}>
                      <img src={url} alt={`Screenshot ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#f1f5f9' }} />
                      <Box className="overlay" sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.2s' }}>
                        <OpenInNewIcon sx={{ color: '#fff' }} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Actions */}
            {(canStart || canRetry || canApprove || canReject) && (
              <Box sx={{ display: 'flex', gap: 1.5, mt: 1, pt: 1.5, borderTop: '1px solid #e2e8f0' }}>
                {canStart && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={actionLoading === 'start' ? <CircularProgress size={16} /> : <PlayIcon />}
                    onClick={onStartPipeline}
                    disabled={!!actionLoading}
                    sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' } }}
                  >
                    Start verwerking
                  </Button>
                )}
                {canRetry && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={actionLoading === 'start' ? <CircularProgress size={16} /> : <RefreshIcon />}
                    onClick={onStartPipeline}
                    disabled={!!actionLoading}
                    sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}
                  >
                    Opnieuw proberen
                  </Button>
                )}
                {canApproveReview && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={actionLoading === 'approve_review' ? <CircularProgress size={16} /> : <ApproveIcon />}
                    onClick={onApproveReview}
                    disabled={!!actionLoading}
                    sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
                  >
                    Plan goedkeuren → implementeren
                  </Button>
                )}
                {canApprove && (
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={actionLoading === 'approve' ? <CircularProgress size={16} /> : <ApproveIcon />}
                    onClick={onApprove}
                    disabled={!!actionLoading}
                  >
                    Goedkeuren voor productie
                  </Button>
                )}
                {canReject && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={actionLoading === 'reject' ? <CircularProgress size={16} /> : <RejectIcon />}
                    onClick={onReject}
                    disabled={!!actionLoading}
                  >
                    Afwijzen
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}
